import { useTheme } from '@mui/material/styles'
import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useFormContext, Controller } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from 'common-ui/components/Tooltip'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import IconButton from '@mui/material/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import codeTheme from '../../themes/code'
import { EnrichedUser } from '../../api/nm-types'
import { ApplianceRealm, Region } from 'common/api/v1/types'
import Alert from '@mui/material/Alert'
import Paper from '@mui/material/Paper'
import { Api } from '../../store'
import Pendable from 'common-ui/components/Pendable'
import Title from '../common/Dialog/Title'
import { Autocomplete } from '../common/Form'
import RHF from '../common/Form/RHF'

const CopyButton = ({
  onCopy,
  showCopied,
  size = 'medium',
}: {
  onCopy: () => void
  showCopied: boolean
  size?: 'small' | 'medium'
}) => {
  if (!navigator.clipboard) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        variant="body2"
        sx={{
          mr: 1,
          opacity: showCopied ? 1 : 0,
          visibility: showCopied ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
        }}
      >
        Copied!
      </Typography>
      <IconButton onClick={onCopy} size={size}>
        <ContentCopyIcon fontSize={size} />
      </IconButton>
    </Box>
  )
}

const SectionHeader = ({ title, tooltip }: { title: string; tooltip: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <Typography variant="subtitle1" sx={{ mr: 1 }}>
      {title}
    </Typography>
    <Tooltip disablePortal={false} title={tooltip}>
      <HelpOutlineIcon color="action" />
    </Tooltip>
  </Box>
)

interface EdgeConnectCommandParams {
  origin: string
  token: string
  options?: {
    name: string
    region: string
    secondaryRegion?: string
  }
}

interface CoreVideoCommandParams {
  origin: string
  token: string
  options?: {
    name: string
    region: string
  }
}

const generateEdgeConnectCommand = ({ origin, token, options }: EdgeConnectCommandParams): string => {
  const { name, region, secondaryRegion } = options ?? {
    name: '<name>',
    region: '<region>',
  }

  let cmd = `curl --output connectit "${origin}/connectit"
chmod +x connectit
sudo SECRET="${token}" ./connectit install edge-connect \\
  --url "${origin}" \\
  --name "${name}" \\
  --region "${region}"`

  if (secondaryRegion) {
    cmd += ` \\
  --region-secondary "${secondaryRegion}"`
  }
  return cmd
}

const generateCoreVideoCommand = ({ origin, token, options }: CoreVideoCommandParams): string => {
  const { name, region } = options ?? {
    name: '<name>',
    region: '<region>',
  }

  return `curl --output connectit "${origin}/connectit"
chmod +x connectit
sudo SECRET="${token}" ./connectit install video \\
  --url "${origin}" \\
  --name "${name}" \\
  --region "${region}"`
}

export type ApplianceType = 'edge' | 'core'

const getTokenRealm = (type: ApplianceType): ApplianceRealm => {
  if (type === 'edge') return ApplianceRealm.edge
  return ApplianceRealm.core
}

interface FormData {
  name: string
  region: Region | null
  secondaryRegion: Region | null
}

const defaultFormValues: FormData = {
  name: '',
  region: null,
  secondaryRegion: null,
}

interface AddApplianceFormContentProps {
  token: string
  expiresAt: Date
  applianceType: ApplianceType
  defaultRegion: Region | null
}

const AddApplianceFormContent = ({ token, expiresAt, applianceType, defaultRegion }: AddApplianceFormContentProps) => {
  const theme = useTheme()
  const { watch, getValues, trigger, formState } = useFormContext<FormData>()
  const [showTokenCopied, setShowTokenCopied] = useState(false)
  const [showCommandCopied, setShowCommandCopied] = useState(false)

  const regionApi = useCallback(Api.regionApi.getRegions.bind(Api.regionApi), [])

  const name = watch('name')
  const region = watch('region')
  const secondaryRegion = watch('secondaryRegion')

  const getCommand = (): string => {
    const origin = window.location.origin
    const options =
      formState.isValid && name && region
        ? {
            name,
            region: region.name,
            secondaryRegion: secondaryRegion?.name,
          }
        : undefined
    switch (applianceType) {
      case 'edge':
        return generateEdgeConnectCommand({ origin, token, options })
      case 'core':
        return generateCoreVideoCommand({ origin, token, options })
      default: {
        const exhaustiveCheck: never = applianceType
        throw new Error(`Unhandled appliance type: ${exhaustiveCheck}`)
      }
    }
  }

  const onCopyToken = () => {
    void navigator.clipboard.writeText(token)
    setShowTokenCopied(true)
    setTimeout(() => setShowTokenCopied(false), 1500)
  }

  const onCopyCommand = () => {
    void navigator.clipboard.writeText(getCommand())
    setShowCommandCopied(true)
    setTimeout(() => setShowCommandCopied(false), 1500)
  }

  const cmd = getCommand()
  const isValid = formState.isValid

  return (
    <>
      {/* Expiration Note */}
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        The appliance secret can only be used for a single appliance and is valid until {expiresAt.toLocaleString()}.
      </Typography>

      {/* Token Section */}
      <Box sx={{ mb: 3 }}>
        <SectionHeader
          title="Appliance secret"
          tooltip={
            <Typography variant="inherit">Use this secret when connecting an appliance to Nimbra Edge.</Typography>
          }
        />
        <Paper
          variant="outlined"
          sx={{
            mt: 1.6,
            pl: 1.5,
            pr: 1,
            pt: 0.6,
            pb: 0.6,
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              mr: 2,
            }}
          >
            {token}
          </Typography>
          <CopyButton onCopy={onCopyToken} showCopied={showTokenCopied} size="small" />
        </Paper>
      </Box>

      <SectionHeader
        title="Install command"
        tooltip={
          <>
            Run this command on a linux machine to use it as an appliance.
            <br />
            Docker must be installed before running the command.
          </>
        }
      />

      {/* Configuration Section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: 'fit-content', alignItems: 'flex-start' }}>
          <Controller
            name="name"
            rules={{
              required: "Can't be blank",
              pattern: {
                value: /^[a-zA-Z0-9_.-]+$/,
                message: 'Only alphanumeric and -._ allowed',
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Name"
                required
                variant="outlined"
                error={fieldState.error !== undefined}
                helperText={fieldState.error?.message}
                size="small"
                margin="normal"
                sx={{ width: '250px' }}
              />
            )}
          />
          <Box sx={{ width: '200px' }}>
            <Autocomplete<Region>
              name="region"
              label="Region"
              required
              api={regionApi}
              getOptionLabel={(r) => r.name}
              getOptionValue={(r) => r}
              optionComparator={(o1, o2) => o1.id === o2.id}
              margin="normal"
              xs={false}
              size="small"
              onChange={() => void trigger('secondaryRegion')}
              defaultOption={defaultRegion ?? undefined}
            />
          </Box>
          {applianceType === 'edge' && (
            <Box sx={{ width: '250px' }}>
              <Autocomplete<Region>
                name="secondaryRegion"
                label="Secondary Region"
                api={regionApi}
                getOptionLabel={(r) => r.name}
                getOptionValue={(r) => r}
                optionComparator={(o1, o2) => o1.id === o2.id}
                margin="normal"
                xs={false}
                size="small"
                validateFn={(value) => {
                  const currentRegion = getValues('region')
                  if (value && currentRegion && value.id === currentRegion.id) {
                    return 'Cannot be the same as the region'
                  }
                  return undefined
                }}
                onChange={() => void trigger('secondaryRegion')}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Command Section */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
          {isValid && (
            <Box
              sx={{
                position: 'absolute',
                mt: 2,
                mr: 1,
                top: 4,
                right: 4,
                zIndex: 2,
              }}
            >
              <CopyButton onCopy={onCopyCommand} showCopied={showCommandCopied} />
            </Box>
          )}
          {!isValid && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                userSelect: 'none',
                pointerEvents: 'none',
                fontStyle: 'italic',
              }}
            >
              The command will show when all fields are valid
            </Typography>
          )}
          <SyntaxHighlighter
            language="bash"
            style={codeTheme}
            codeTagProps={{
              style: {
                backgroundColor: 'transparent',
                opacity: isValid ? 1 : 0.3,
                filter: isValid ? 'none' : 'blur(2px)',
                userSelect: isValid ? 'auto' : 'none',
              },
            }}
            customStyle={{
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              border: '1px solid ' + theme.palette.divider,
              width: 'auto',
            }}
          >
            {cmd}
          </SyntaxHighlighter>
        </Box>
      </Box>
    </>
  )
}

export const AddApplianceDialog = ({
  open,
  onClose,
  user,
  applianceType,
}: {
  open: boolean
  onClose: () => void
  user: EnrichedUser
  applianceType: ApplianceType
}) => {
  const tokenRealm = getTokenRealm(applianceType)

  const {
    data: fetchedToken,
    isLoading: tokenLoading,
    error: tokenError,
  } = useQuery({
    queryKey: ['appliance-token', tokenRealm, user.group],
    queryFn: () => Api.groupsApi.createApplianceToken(user.group, { realm: tokenRealm }),
    enabled: open,
    staleTime: Infinity, // Never refetch while dialog is open
    meta: { operation: 'create appliance token' },
  })

  const { data: regionsData, isLoading: regionsLoading } = useQuery({
    queryKey: ['regions-for-default'],
    queryFn: () => Api.regionApi.getRegions({ pageNumber: '0', rowsPerPage: '500' }),
    enabled: open,
    meta: { operation: 'fetch regions' },
  })

  const defaultRegion = regionsData?.items.find((r) => r.default_region) ?? null

  const renderContent = () => {
    if (tokenLoading || regionsLoading) {
      return <Pendable pending>{null}</Pendable>
    }

    if (tokenError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tokenError.message}
        </Alert>
      )
    }

    if (!fetchedToken) return null

    const formDefaultValues: FormData = {
      ...defaultFormValues,
      region: defaultRegion,
    }

    return (
      <RHF<FormData>
        defaultValues={formDefaultValues}
        enableReinitialize={!open}
        onSubmit={() => {
          // No submission - this form is just for generating a command
        }}
      >
        <AddApplianceFormContent
          token={fetchedToken.token}
          expiresAt={fetchedToken.expiresAt}
          applianceType={applianceType}
          defaultRegion={defaultRegion}
        />
      </RHF>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableRestoreFocus>
      <Title title="Add Appliance" onClose={onClose} />
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
